import React, { useState, useCallback } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, Search, Building2 } from 'lucide-react';
import { Area } from '../../types';
import ApiService from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';

interface AreasConfigProps {
  onBack: () => void;
}

interface AreaFormData {
  nombre: string;
  descripcion: string;
  unidadNegocioId: number | null;
}

const AreasConfig: React.FC<AreasConfigProps> = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [formData, setFormData] = useState<AreaFormData>({ nombre: '', descripcion: '', unidadNegocioId: null });

  // Memoizar función de API para evitar recreaciones
  const fetchAreas = useCallback(() => ApiService.getAreas(), []);
  const fetchUnidadesNegocio = useCallback(() => ApiService.getUnidadesNegocio(), []);

  // Hooks para datos y operaciones
  const { data: areas, loading, error, refetch } = useApi(fetchAreas);
  const { data: unidadesNegocio } = useApi(fetchUnidadesNegocio);
  const { execute: createArea, loading: creating } = useAsyncOperation();
  const { execute: updateArea, loading: updating } = useAsyncOperation();
  const { execute: deleteArea } = useAsyncOperation();

  // Filtrar áreas por búsqueda
  const filteredAreas = areas?.filter(area =>
    area.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (area.descripcion && area.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Manejar apertura de modal para crear/editar
  const handleOpenModal = (area?: Area) => {
    if (area) {
      setEditingArea(area);
      setFormData({ 
        nombre: area.nombre, 
        descripcion: area.descripcion || '',
        unidadNegocioId: area.unidadNegocio?.id || null
      });
    } else {
      setEditingArea(null);
      setFormData({ nombre: '', descripcion: '', unidadNegocioId: null });
    }
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingArea(null);
    setFormData({ nombre: '', descripcion: '', unidadNegocioId: null });
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      alert('El nombre del área es obligatorio');
      return;
    }

    try {
      if (editingArea) {
        // Actualizar área existente
        await updateArea(() => 
          ApiService.updateArea(editingArea.id, { ...formData, activa: true })
        );
      } else {
        // Crear nueva área
        await createArea(() => ApiService.createArea(formData));
      }
      
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error('Error al guardar área:', error);
    }
  };

  // Eliminar área
  const handleDelete = async (id: number, nombre: string) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el área "${nombre}"?`)) {
      return;
    }

    try {
      await deleteArea(() => ApiService.deleteArea(id));
      refetch();
    } catch (error) {
      console.error('Error al eliminar área:', error);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
          <div className="flex items-center space-x-2">
            <Building2 className="w-6 h-6 text-primary dark:text-primary-dark" />
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">Gestión de Áreas</h1>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Nueva Área</span>
        </button>
      </div>

      {/* Búsqueda */}
      <div className="card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar áreas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Lista de áreas */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Área
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Unidad de Negocio
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-700">
              {filteredAreas.map((area) => (
                <tr key={area.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-neutral-900 dark:text-neutral-50">{area.nombre}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400">{area.descripcion || 'Sin descripción'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400">
                      {area.unidadNegocio ? (
                        <span className="badge text-xs">
                          {area.unidadNegocio.nombre}
                        </span>
                      ) : (
                        <span className="text-neutral-400 dark:text-neutral-500">Sin asignar</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(area)}
                        className="text-primary dark:text-primary-dark hover:text-primary-hover dark:hover:text-primary-dark p-1 rounded transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(area.id, area.nombre)}
                        className="text-error hover:text-error-hover p-1 rounded transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAreas.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-50">No hay áreas</h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                {searchTerm ? 'No se encontraron áreas con ese criterio' : 'Comienza creando una nueva área'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar área */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-neutral-900/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 w-full max-w-md shadow-md">
            <h2 className="text-xl font-bold mb-4 text-neutral-900 dark:text-neutral-50">
              {editingArea ? 'Editar Área' : 'Nueva Área'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Nombre del Área *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="input-field w-full"
                  placeholder="Ej: Desarrollo, Marketing, etc."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="input-field w-full resize-none"
                  rows={3}
                  placeholder="Descripción del área y sus responsabilidades..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Unidad de Negocio
                </label>
                <select
                  value={formData.unidadNegocioId || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    unidadNegocioId: e.target.value ? parseInt(e.target.value) : null
                  })}
                  className="input-field w-full"
                >
                  <option value="">Seleccionar unidad de negocio...</option>
                  {unidadesNegocio?.map((unidad) => (
                    <option key={unidad.id} value={unidad.id}>
                      {unidad.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="btn-primary disabled:opacity-50"
                >
                  {creating || updating ? 'Guardando...' : editingArea ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreasConfig;